import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { IndianRupee, Plus, Check, Clock, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { HaafPaymentsTable } from "@/pages/dashboard/admin/haaf-payments/components/haaf-payments-table";
import { AddHaafPaymentForm } from "@/pages/dashboard/admin/haaf-payments/components/add-haaf-payment-form";
import {
  FinancialYearSelector,
} from "@/pages/dashboard/admin/haaf-payments/components/financial-year-selector";
import { useSelectedHaafFinancialYear } from "@/pages/dashboard/admin/haaf-payments/components/use-selected-haaf-financial-year";
import { HaafReceiptModal } from "@/components/haaf-payment/HaafReceiptModal";
import {
  useChapterHaafPayments,
  useUpdateHaafPayment,
  useDeleteHaafPayment,
  useHaafPaymentBatchStats,
  useHaafPaymentChapterStats,
} from "@/api/haaf-payments";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { isWithinInterval, startOfDay, endOfDay } from "date-fns";
import { currencies } from "@/lib/constants";
import { useDebounce } from "@/hooks/useDebounce";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

const ITEMS_PER_LOAD = 20;

export default function HafPayments() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [displayedItemsCount, setDisplayedItemsCount] =
    useState(ITEMS_PER_LOAD);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Determine active tab from URL - check for exact path match
  const currentPath = location.pathname;
  const activeTab = currentPath.endsWith("/statistics")
    ? "statistics"
    : "payments";

  const {
    financialYears,
    isFinancialYearsLoading,
    selectedFinancialYearId,
    setSelectedFinancialYearId,
  } = useSelectedHaafFinancialYear();
  const { data: payments, isLoading } = useChapterHaafPayments(
    selectedFinancialYearId,
  );
  const { data: batchStats, isLoading: isBatchStatsLoading } =
    useHaafPaymentBatchStats(selectedFinancialYearId);
  const { data: chapterStats, isLoading: isChapterStatsLoading } =
    useHaafPaymentChapterStats(selectedFinancialYearId);

  // Handle tab changes via URL navigation
  const handleTabChange = useCallback(
    (newTab) => {
      if (newTab === "payments") {
        navigate("/haaf-coordinator/haf-payments");
      } else if (newTab === "statistics") {
        navigate("/haaf-coordinator/haf-payments/statistics");
      }
    },
    [navigate]
  );

  const updateMutation = useUpdateHaafPayment();
  const deleteMutation = useDeleteHaafPayment();
  const [editPayment, setEditPayment] = useState(null);
  const [receiptPayment, setReceiptPayment] = useState(null);

  // Coordinator has limited permissions - only view and edit, no verification or deletion
  const handleEdit = useCallback((payment) => {
    setEditPayment(payment);
  }, []);

  const handleDelete = useCallback(
    (payment) => {
      const confirmed = window.confirm(
        "Permanently delete this HAF payment? This cannot be undone.",
      );
      if (!confirmed) return;
      deleteMutation.mutate(payment._id || payment.paymentId);
    },
    [deleteMutation],
  );

  const handleDownloadReceipt = useCallback((payment) => {
    setReceiptPayment(payment);
  }, []);

  const handleReceiptOpen = useCallback((receipt) => {
    setSelectedReceipt(receipt);
    setReceiptModalOpen(true);
  }, []);

  const handleReceiptClose = useCallback(() => {
    setSelectedReceipt(null);
    setReceiptModalOpen(false);
  }, []);

  // Get unique batch values for filter
  const batches = useMemo(() => {
    if (!payments) return [];
    const batchSet = new Set(payments.map((payment) => payment.batch));
    return ["all", ...Array.from(batchSet)];
  }, [payments]);

  // Optimize filtering and sorting with useMemo
  const { filteredPayments, displayedPayments, hasMore } = useMemo(() => {
    if (!payments)
      return { filteredPayments: [], displayedPayments: [], hasMore: false };

    // Filter payments
    const filtered = payments.filter((payment) => {
      // Search filter
      if (debouncedSearchQuery) {
        const query = debouncedSearchQuery.toLowerCase();
        const matchesPaymentId = payment.paymentId
          .toLowerCase()
          .includes(query);
        const matchesDonorName = payment.donorName
          ?.toLowerCase()
          .includes(query);
        const matchesCollectorName = payment.collectorName
          ?.toLowerCase()
          .includes(query);
        const matchesChapterName = payment.chapterName
          ?.toLowerCase()
          .includes(query);
        return (
          matchesPaymentId ||
          matchesDonorName ||
          matchesCollectorName ||
          matchesChapterName
        );
      }

      // Batch filter
      if (selectedBatch !== "all" && payment.batch !== selectedBatch) {
        return false;
      }

      // Date filter
      if (dateRange.from && dateRange.to) {
        const paymentDate = new Date(payment.createdAt);
        return isWithinInterval(paymentDate, {
          start: startOfDay(dateRange.from),
          end: endOfDay(dateRange.to),
        });
      }

      return true;
    });

    // Sort payments
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });

    // Get displayed items based on current count
    const displayed = sorted.slice(0, displayedItemsCount);
    const hasMoreItems = sorted.length > displayedItemsCount;

    return {
      filteredPayments: filtered,
      displayedPayments: displayed,
      hasMore: hasMoreItems,
    };
  }, [
    payments,
    debouncedSearchQuery,
    selectedBatch,
    dateRange,
    sortOrder,
    displayedItemsCount,
  ]);

  // Load more items
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    // Simulate loading delay for better UX
    setTimeout(() => {
      setDisplayedItemsCount((prev) => prev + ITEMS_PER_LOAD);
      setIsLoadingMore(false);
    }, 300);
  }, [isLoadingMore, hasMore]);

  // Window scroll event handler
  const handleScroll = useCallback(() => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    const isNearBottom = windowHeight + scrollTop >= documentHeight - 200;

    if (isNearBottom && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore]);

  // Add window scroll listener
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Reset displayed items when filters change
  const handleBatchChange = useCallback((newBatch) => {
    setSelectedBatch(newBatch);
    setDisplayedItemsCount(ITEMS_PER_LOAD);
  }, []);

  const handleSortChange = useCallback((newSort) => {
    setSortOrder(newSort);
    setDisplayedItemsCount(ITEMS_PER_LOAD);
  }, []);

  const handleDateRangeChange = useCallback((field, date) => {
    setDateRange((prev) => ({ ...prev, [field]: date }));
    setDisplayedItemsCount(ITEMS_PER_LOAD);
  }, []);

  // Reset when search changes
  useEffect(() => {
    setDisplayedItemsCount(ITEMS_PER_LOAD);
  }, [debouncedSearchQuery]);

  // Calculate stats with useMemo for better performance
  const paymentsStats = useMemo(
    () => [
      {
        title: "Chapter Collections",
        value:
          filteredPayments?.reduce((acc, curr) => {
            const byCurrency = acc[curr.currency] || 0;
            acc[curr.currency] = byCurrency + curr.amount;
            return acc;
          }, {}) || {},
        icon: IndianRupee,
        description: "Total amount collected by chapter",
        isCurrencyGroup: true,
      },
      {
        title: "Verified Payments",
        value: filteredPayments?.filter((p) => p.verified).length || 0,
        icon: Check,
        description: "Number of verified payments",
      },
      {
        title: "Total Payments",
        value: filteredPayments?.length || 0,
        icon: Clock,
        description: "Total payment records",
      },
    ],
    [filteredPayments]
  );

  if (isLoading || isFinancialYearsLoading) {
    return (
      <div className="flex h-[200px] w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6 bg-slate-50 dark:bg-slate-900 min-h-screen">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-4"
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-indigo-500 text-transparent bg-clip-text">
              Chapter HAF Payments
            </h2>
            <p className="text-sm text-muted-foreground">
              View and track HAF payment collections from your chapter
              collectors
            </p>
          </div>

          <div className="flex items-center gap-2">
            <FinancialYearSelector
              financialYears={financialYears}
              selectedFinancialYearId={selectedFinancialYearId}
              onChange={setSelectedFinancialYearId}
            />
            <TabsList className="bg-slate-200 dark:bg-slate-800 p-1 rounded-lg">
              <TabsTrigger
                value="payments"
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 rounded-md transition-all duration-200"
              >
                Payments
              </TabsTrigger>
              <TabsTrigger
                value="statistics"
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 rounded-md transition-all duration-200"
              >
                Statistics
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value="payments" className="space-y-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
            {/* Hide filters on mobile, show only on desktop */}
            <div className="hidden md:flex flex-col gap-2 sm:flex-row sm:items-center w-full justify-between">
              <div className="flex flex-wrap gap-2">
                <DatePicker
                  selected={dateRange.from}
                  onSelect={(date) => handleDateRangeChange("from", date)}
                  placeholder="From Date"
                  className="w-full sm:w-auto"
                />
                <DatePicker
                  selected={dateRange.to}
                  onSelect={(date) => handleDateRangeChange("to", date)}
                  placeholder="To Date"
                  className="w-full sm:w-auto"
                />
                <Select value={selectedBatch} onValueChange={handleBatchChange}>
                  <SelectTrigger className="w-full sm:w-[180px] bg-white dark:bg-slate-900">
                    <SelectValue placeholder="Filter by batch" />
                  </SelectTrigger>
                  <SelectContent>
                    {batches.map((batch) => (
                      <SelectItem key={batch} value={batch}>
                        {batch === "all" ? "All Batches" : batch}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={sortOrder} onValueChange={handleSortChange}>
                  <SelectTrigger className="w-full sm:w-[180px] bg-white dark:bg-slate-900">
                    <SelectValue placeholder="Sort by date" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc">Newest First</SelectItem>
                    <SelectItem value="asc">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                className="hidden md:flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                onClick={() => setOpen(true)}
              >
                <Plus className="h-4 w-4" /> Add HAF Payment
              </Button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {paymentsStats.map((stat) => (
              <Card
                key={stat.title}
                className="overflow-hidden border-0 shadow-md bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-900 hover:shadow-lg transition-shadow duration-200"
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 border-b">
                  <CardTitle className="text-sm font-medium">
                    {stat.title}
                  </CardTitle>
                  <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                    <stat.icon className="h-4 w-4" />
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {stat.isCurrencyGroup ? (
                    <div className="space-y-2">
                      {Object.entries(stat.value).map(([currency, amount]) => (
                        <div
                          key={currency}
                          className="flex justify-between items-center p-2 rounded-md bg-slate-100 dark:bg-slate-800"
                        >
                          <span className="text-sm font-medium">
                            {currency}
                          </span>
                          <span className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                            {currencies[currency].symbol}
                            {amount.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                        {stat.value}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {stat.description}
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="shadow-md border-0 overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-blue-50 to-slate-50 dark:from-slate-800 dark:to-slate-900 border-b">
              <CardTitle className="text-blue-700 dark:text-blue-400 font-semibold flex items-center">
                <span className="mr-2">Chapter HAF Payments</span>
                {displayedPayments.length > 0 && (
                  <span className="text-xs py-0.5 px-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                    {displayedPayments.length}
                  </span>
                )}
              </CardTitle>
              <div className="text-sm text-muted-foreground">
                Showing {displayedPayments.length} of {filteredPayments.length}{" "}
                payments
                {hasMore && (
                  <span className="ml-1 text-blue-600 dark:text-blue-400 font-medium">
                    (scroll for more)
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <HaafPaymentsTable
                data={displayedPayments}
                onVerificationToggle={() => {}} // No verification for coordinators
                searchQuery={searchQuery}
                onSearch={(e) => setSearchQuery(e.target.value)}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onDownloadReceipt={handleDownloadReceipt}
                onReceiptOpen={handleReceiptOpen}
                showActions={true}
                hideVerificationToggle={true} // Hide verification controls for coordinators
                isLoading={updateMutation.isLoading}
              />

              {/* Loading more indicator */}
              {isLoadingMore && (
                <div className="flex justify-center items-center py-4 bg-slate-50 dark:bg-slate-800">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                  <span className="ml-2 text-sm text-blue-600 dark:text-blue-400 font-medium">
                    Loading more...
                  </span>
                </div>
              )}

              {/* End of results indicator */}
              {!hasMore && filteredPayments.length > 0 && (
                <div className="text-center py-4 text-sm text-slate-500 bg-slate-50 dark:bg-slate-800 dark:text-slate-400 border-t">
                  You&apos;ve reached the end of the list
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statistics" className="space-y-6">
          {isBatchStatsLoading || isChapterStatsLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-2 text-blue-600 dark:text-blue-400">
                Loading statistics...
              </span>
            </div>
          ) : (
            <Tabs defaultValue="batch" className="space-y-4">
              <TabsList className="bg-slate-200 dark:bg-slate-800 p-1 rounded-lg">
                <TabsTrigger
                  value="batch"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 rounded-md transition-all duration-200"
                >
                  Batch Statistics
                </TabsTrigger>
                <TabsTrigger
                  value="chapter"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 rounded-md transition-all duration-200"
                >
                  Chapter Statistics
                </TabsTrigger>
              </TabsList>

              <TabsContent value="batch">
                <Card className="shadow-md border-0 overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 dark:from-slate-800 dark:to-slate-900 border-b">
                    <CardTitle className="text-blue-700 dark:text-blue-400 font-semibold flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Verified Payments by Batch
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <ChartContainer
                      config={{
                        amount: {
                          label: "Verified Amount",
                          color: "hsl(217, 91%, 60%)",
                        },
                      }}
                      className="h-[600px]"
                    >
                      <BarChart
                        data={
                          batchStats?.map((stat) => ({
                            name: stat._id || "Unknown Batch",
                            amount: stat.totalVerifiedAmount,
                          })) || []
                        }
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="name"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={0}
                        />
                        <YAxis
                          tickFormatter={(value) =>
                            `₹${value.toLocaleString()}`
                          }
                        />
                        <ChartTooltip
                          content={<ChartTooltipContent />}
                          formatter={(value) => [
                            `₹${value.toLocaleString()}`,
                            "Verified Amount",
                          ]}
                        />
                        <Bar
                          dataKey="amount"
                          fill="var(--color-amount)"
                          name="Verified Amount"
                        />
                      </BarChart>
                    </ChartContainer>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="chapter">
                <Card className="shadow-md border-0 overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 dark:from-slate-800 dark:to-slate-900 border-b">
                    <CardTitle className="text-blue-700 dark:text-blue-400 font-semibold flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Verified Payments by Chapter
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <ChartContainer
                      config={{
                        amount: {
                          label: "Verified Amount",
                          color: "hsl(142, 69%, 58%)",
                        },
                      }}
                      className="h-[600px]"
                    >
                      <BarChart
                        data={
                          chapterStats?.map((stat) => ({
                            name: stat.chapterName || "Unknown Chapter",
                            amount: stat.totalVerifiedAmount,
                          })) || []
                        }
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="name"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={0}
                          fontSize={12}
                        />
                        <YAxis
                          tickFormatter={(value) =>
                            `₹${value.toLocaleString()}`
                          }
                        />
                        <ChartTooltip
                          content={<ChartTooltipContent />}
                          formatter={(value) => [
                            `₹${value.toLocaleString()}`,
                            "Verified Amount",
                          ]}
                        />
                        <Bar
                          dataKey="amount"
                          fill="var(--color-amount)"
                          name="Verified Amount"
                        />
                      </BarChart>
                    </ChartContainer>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </TabsContent>
      </Tabs>

      {/* Mobile Add Payment FAB */}
      <Button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 h-14 w-14 rounded-full shadow-lg md:hidden bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 hover:scale-105 hover:shadow-blue-300 dark:hover:shadow-blue-900"
        size="icon"
      >
        <Plus className="h-6 w-6" />
      </Button>

      {/* Centralized Add HAF Payment Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl p-4 pt-6 bg-white dark:bg-slate-900 border-0 shadow-xl">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="text-xl font-bold text-blue-700 dark:text-blue-400">
              Add New HAF Payment
            </DialogTitle>
            <DialogDescription className="text-xs">
              Enter the HAF payment details
            </DialogDescription>
          </DialogHeader>
          <AddHaafPaymentForm
            financialYearId={selectedFinancialYearId}
            onSuccess={() => {
              setOpen(false);
              console.log("Form submitted successfully, closing modal");
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit HAF Payment Dialog */}
      <Dialog
        open={!!editPayment}
        onOpenChange={(open) => !open && setEditPayment(null)}
      >
        <DialogContent className="max-w-2xl p-4 pt-6 bg-white dark:bg-slate-900 border-0 shadow-xl">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="text-xl font-bold text-blue-700 dark:text-blue-400">
              Edit HAF Payment
            </DialogTitle>
            <DialogDescription className="text-xs">
              Update the HAF payment details
            </DialogDescription>
          </DialogHeader>
          {editPayment && (
            <AddHaafPaymentForm
              initialData={editPayment}
              financialYearId={selectedFinancialYearId}
              onSuccess={() => {
                setEditPayment(null);
                console.log("Edit form submitted successfully, closing modal");
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog
        open={receiptModalOpen}
        onOpenChange={handleReceiptClose}
        className="relative z-50"
      >
        <DialogContent className="max-w-3xl p-4 pt-6 bg-white dark:bg-slate-900 border-0 shadow-xl">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="text-xl font-bold text-blue-700 dark:text-blue-400">
              Payment Receipt
            </DialogTitle>
            <DialogDescription className="text-xs">
              View and download the payment receipt
            </DialogDescription>
          </DialogHeader>
          {selectedReceipt && (
            <div className="mt-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                Receipt Details
              </h3>
              <div className="mt-2 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    Payment ID:
                  </span>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {selectedReceipt.paymentId}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Amount:</span>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {currencies[selectedReceipt.currency].symbol}
                    {selectedReceipt.amount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    Currency:
                  </span>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {selectedReceipt.currency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Date:</span>
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {new Date(selectedReceipt.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="mt-4">
                <Button
                  onClick={() => {
                    // Handle download receipt logic
                    console.log("Download receipt clicked");
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                >
                  Download Receipt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Haaf Receipt Modal */}
      <HaafReceiptModal
        isOpen={!!receiptPayment}
        onClose={() => setReceiptPayment(null)}
        payment={receiptPayment}
      />
    </div>
  );
}
